import React, { useState } from "react";
import { readiness } from "../lib/readiness";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { formatAED, bedroomsLabel } from "../lib/api";
import { variantSrcSet, dropVariants } from "../lib/imgVariants";

const WA_NUMBER = "971547054704";
const waHref = (p) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(`Hi Sofia Sands, I'm interested in ${p.title} (https://www.sofiasandsrealty.ae/listings/${p.slug})`)}`;

export default function PropertyCard({ p, index = 0, variant = "tile" }) {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div className="relative">
    <Link
      to={`/listings/${p.slug}`}
      className="group block bg-bone relative"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`property-card-${p.slug}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/5] overflow-hidden bg-sand">
        {/* Blur placeholder */}
        {!imgLoaded && (
          <div className="absolute inset-0 bg-sand shimmer-bg" />
        )}
        <img
          src={p.hero_image}
          srcSet={variantSrcSet(p.hero_image)}
          sizes="(max-width: 768px) 100vw, 33vw"
          width={1200}
          height={800}
          alt={p.title}
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={(e) => {
            if (dropVariants(e.currentTarget)) return;
            if (!e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = "1";
              e.currentTarget.src = "/images/placeholders/dubai-skyline.jpg";
            }
            setImgLoaded(true);
          }}
          className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-[1.06] ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
        {/* Stamps */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <span className="stamp stamp-rera" data-testid={`card-rera-${p.slug}`}>RERA 41793</span>
        </div>
        {readiness(p) && (
          <div className="absolute top-4 right-4">
            <span className="stamp bg-bone/85 text-ink backdrop-blur-sm" data-testid={`card-status-${p.slug}`}>
              {readiness(p)}
            </span>
          </div>
        )}

        {/* Bottom scrim */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink/55 to-transparent pointer-events-none" />

        {/* 'View residence' reveal label (bottom-right) */}
        <div className="absolute bottom-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out">
          <span className="inline-flex items-center gap-1.5 bg-bone text-ink px-3 py-1.5 text-[0.62rem] tracking-caps uppercase font-medium">
            View residence <ArrowUpRight size={12} />
          </span>
        </div>

        {/* Editorial italic caption (bottom-left) */}
        <div className="absolute bottom-4 left-4 opacity-80 group-hover:opacity-100 transition-opacity">
          <span className="font-display italic text-[0.88rem] text-bone leading-none tracking-tighter">
            — {p.community || p.location}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="pt-6 pb-2">
        <div className="flex items-center justify-between">
          <span className="overline overline-muted">{p.region} &middot; {p.community || p.location}</span>
          {p.net_yield_pct ? (
            <span className="text-[0.72rem] tracking-caps uppercase text-gold font-medium">
              {p.net_yield_pct}% yield
            </span>
          ) : null}
        </div>

        <h3 className="font-display text-[1.85rem] leading-[1.12] mt-3 text-ink tracking-tighter">
          {p.title}
        </h3>

        {/* Gold hairline that draws in on hover */}
        <div className="relative mt-4 mb-4 h-px bg-sand-dark overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-0 bg-gold transition-[width] duration-700 ease-out group-hover:w-full" />
        </div>

        <div className="flex items-end justify-between">
          <div>
            <div className="font-display text-[1.4rem] text-ink leading-none" data-testid={`card-price-${p.slug}`}>
              {formatAED(p.price_aed ?? p.price)}
            </div>
            <div className="text-[0.78rem] text-ink/60 mt-2 font-light">
              {bedroomsLabel(p.bedrooms)} &middot; {(p.size_sqft ?? p.sqft ?? 0).toLocaleString()} sqft
            </div>
          </div>
          <div className="flex items-center gap-1 text-[0.7rem] tracking-caps uppercase font-medium text-ink group-hover:text-gold transition-colors">
            View <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </Link>
      <a
        href={waHref(p)}
        target="_blank"
        rel="noopener"
        data-lead-placement="card"
        data-testid={`card-whatsapp-${p.slug}`}
        className="mt-3 inline-flex items-center gap-1.5 text-[0.68rem] tracking-caps uppercase font-medium text-gold hover:text-ink transition-colors"
        aria-label={`WhatsApp Sofia Sands about ${p.title}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2m0 18.15a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24s8.24 3.7 8.24 8.24-3.7 8.24-8.24 8.24m4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.37 1 2.54.12.17 1.73 2.64 4.2 3.7.59.25 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28"/></svg>
        WhatsApp about this
      </a>
    </div>
  );
}
